/**
 * Row-label widths, shared by the three charts that have a label column — `DimensionBars`,
 * `PairedBars` and `UnitMatrix` (layout round, 2 October 2026).
 *
 * SVG has no `text-overflow`, so a label too long for its column has to be cut here rather
 * than by the renderer. Every caller keeps the full text in the row's own `<title>`, so an
 * ellipsis never loses a name — it only stops one watershed from writing over the track.
 */
import { LABEL_LINE_H, labelColumn, type LabelColumn } from '../../../components/charts/labels';
import { chartFont, textWidth, wrapText } from '../../../lib/text';

/** The label column, in viewBox units; the bar charts and the matrix share it. */
export const LABEL_COL_W = 118;

/**
 * Average glyph width at the label's 11.5 px. Deliberately generous (a label of mostly
 * narrow letters keeps a character or two of slack) because an over-long label overrunning
 * the track is the one failure a reader cannot read around.
 */
export const LABEL_CHAR_W = 5.6;

export const LABEL_MAX_CHARS = Math.floor(LABEL_COL_W / LABEL_CHAR_W);

/** The label as the column can hold it, with an ellipsis when it cannot hold all of it. */
export function clipLabel(label: string, maxChars = LABEL_MAX_CHARS): string {
  return label.length <= maxChars
    ? label
    : `${label.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;
}

/** The label's largest drawn size (the analysis pane's 11.5 px), which wrapping measures at. */
export const LABEL_SIZE = 11.5;

/**
 * The label on the lines the column can hold it on: one, or two when it is longer (launch
 * round, 7 Oct 2026: row names are never cut). `DimensionBars` and `PairedBars` draw these
 * and grow the row; `UnitMatrix`, a grid of fixed cells, still uses `clipLabel`.
 */
export function wrapLabel(label: string, width = LABEL_COL_W - 2): string[] {
  return wrapText(label, width, chartFont(LABEL_SIZE), 2);
}

/** The extra height a wrapped label adds to its row. */
export function wrapExtra(lines: readonly string[]): number {
  return (Math.max(1, lines.length) - 1) * LABEL_SIZE * LABEL_LINE_H;
}

/*
 * #749 (7 Oct 2026, "rigid/fixed and not interactive and responsive"): the charts in this
 * folder lay out in real pixels at their measured width, so the helpers below size their
 * columns from measured text rather than from fixed viewBox units.
 */

/** The size every row label is drawn at, in pixels, at every width. */
export const ROW_LABEL_PX = 11;
/** The size of a printed value (mono). */
export const VALUE_PX = 11;
/** Below this width a chart drops to its compact mode (fewer secondary labels). */
export const COMPACT_W = 320;
/** The lightbox draws these charts this much larger than one pixel per unit. */
export const INTEL_TEXT_ZOOM = 1.25;

/**
 * The label column for `labels` in a chart `w` px wide: as wide as the longest name needs,
 * at least `LABEL_COL_W` (or 30 % of a narrow chart), at most 45 % of the chart; longer
 * names wrap to two lines. The `components/charts/labels` rule, at this folder's 11 px.
 */
export function rowLabelColumn(
  labels: readonly string[],
  w: number,
  { min = LABEL_COL_W, size = ROW_LABEL_PX }: { min?: number; size?: number } = {},
): LabelColumn {
  return labelColumn(labels, { size, min: Math.min(min, Math.round(w * 0.3)), total: w });
}

/** The width of a printed value in the mono face. */
export function valueWidth(text: string, size = VALUE_PX): number {
  return textWidth(text, size, 400, true);
}

/** `label` cut with an ellipsis to fit `width` px at `size` (the matrix's fixed cells). */
export function fitLabel(label: string, width: number, size = ROW_LABEL_PX): string {
  if (textWidth(label, size) <= width) return label;
  let n = label.length;
  while (n > 1 && textWidth(`${label.slice(0, n).trimEnd()}…`, size) > width) n -= 1;
  return `${label.slice(0, n).trimEnd()}…`;
}

/**
 * Every how-many-th category label fits under the bars: 1 when each label is narrower than
 * its slot, more when they would collide (and always at least 2 in compact mode when there
 * are more than six bars). The first and last labels are always drawn.
 */
export function labelStride(labels: readonly string[], slot: number, compact: boolean): number {
  const widest = labels.reduce((m, l) => Math.max(m, textWidth(l, ROW_LABEL_PX)), 0);
  let stride = Math.max(1, Math.ceil((widest + 6) / Math.max(1, slot)));
  if (compact && labels.length > 6) stride = Math.max(stride, 2);
  return stride;
}

/** Whether label `i` of `n` is drawn at `stride`: the ends always, and every `stride`-th
 * between them that does not crowd the last one. */
export function showsLabel(i: number, n: number, stride: number): boolean {
  if (i === 0 || i === n - 1 || stride <= 1) return true;
  return i % stride === 0 && n - 1 - i >= stride;
}
